import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Keyboard } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default shortcuts
const DEFAULT_SHORTCUTS = [
  { key: 'g d', action: 'Go to Dashboard', category: 'Navigation', path: '/' },
  { key: 'g c', action: 'Go to Command Center', category: 'Navigation', path: '/command-center' },
  { key: 'g s', action: 'Go to Strategic Plan', category: 'Navigation', path: '/strategic-plan' },
  { key: 'g i', action: 'Go to Intelligent Scheduling', category: 'Navigation', path: '/intelligent-scheduling' },
  { key: 'g a', action: 'Go to Ask Your Data', category: 'Navigation', path: '/ask-data' },
  { key: 'g g', action: 'Go to Goal Tracker', category: 'Navigation', path: '/goals' },
  { key: 'g w', action: 'Go to Workforce Intelligence', category: 'Navigation', path: '/workforce-intelligence' },
  { key: '?', action: 'Show shortcuts help', category: 'Help' },
  { key: 'Escape', action: 'Close modal/dialog', category: 'General' },
  { key: 'r', action: 'Refresh current page data', category: 'Actions' },
  { key: 'e', action: 'Export to PDF', category: 'Actions' },
  { key: 'f', action: 'Toggle favorite', category: 'Actions' },
  { key: '/', action: 'Focus search', category: 'General' },
];

/**
 * KeyboardShortcuts - Global keyboard shortcut manager with help dialog
 * Provides navigation shortcuts and action shortcuts
 */
export default function KeyboardShortcuts({ 
  enabled = true,
  onExport,
  onFavorite,
  onRefresh 
}) {
  const [showHelp, setShowHelp] = useState(false);
  const [keySequence, setKeySequence] = useState('');
  const [shortcuts, setShortcuts] = useState(DEFAULT_SHORTCUTS);
  const navigate = useNavigate();
  const location = useLocation();

  // Fetch shortcuts from backend
  useEffect(() => {
    const fetchShortcuts = async () => {
      const token = localStorage.getItem('qapi-token');
      if (!token) return;

      try {
        const res = await fetch(`${API_URL}/api/quick-wins/keyboard-shortcuts`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res.ok) {
          const data = await res.json();
          if (data.shortcuts) {
            // Merge with default paths
            const merged = data.shortcuts.map(s => {
              const defaultShortcut = DEFAULT_SHORTCUTS.find(d => d.key === s.key);
              return { ...s, path: defaultShortcut?.path || null };
            });
            setShortcuts(merged);
          }
        }
      } catch (error) {
        console.error('Failed to fetch shortcuts:', error);
      }
    };

    fetchShortcuts();
  }, []);

  const handleKeyDown = useCallback((event) => {
    if (!enabled) return;
    
    // Ignore if in input field
    const target = event.target;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    const key = event.key.toLowerCase();

    // Show help
    if (key === '?' && !event.shiftKey) {
      event.preventDefault();
      setShowHelp(true);
      return;
    }

    // Escape closes dialogs
    if (key === 'escape') {
      setShowHelp(false);
      setKeySequence('');
      return;
    }

    // Build key sequence (for "g d" type shortcuts)
    const newSequence = keySequence + (keySequence ? ' ' : '') + key;
    setKeySequence(newSequence);

    // Clear sequence after 1 second
    setTimeout(() => setKeySequence(''), 1000);

    // Check for matching shortcut
    const shortcut = shortcuts.find(s => s.key.toLowerCase() === newSequence);
    
    if (shortcut) {
      event.preventDefault();
      setKeySequence('');

      if (shortcut.category === 'Navigation' && shortcut.path) {
        navigate(shortcut.path);
      } else if (shortcut.action === 'Refresh current page data' && onRefresh) {
        onRefresh();
      } else if (shortcut.action === 'Export to PDF' && onExport) {
        onExport();
      } else if (shortcut.action === 'Toggle favorite' && onFavorite) {
        onFavorite();
      } else if (shortcut.action === 'Focus search') {
        const searchInput = document.querySelector('[data-testid="command-bar-btn"]');
        if (searchInput) searchInput.click();
      }
    }
  }, [enabled, keySequence, shortcuts, navigate, onExport, onFavorite, onRefresh]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.category]) {
      acc[shortcut.category] = [];
    }
    acc[shortcut.category].push(shortcut);
    return acc;
  }, {});

  return (
    <>
      {/* Keyboard indicator with current sequence */}
      {keySequence && (
        <div 
          className="fixed bottom-4 right-4 bg-slate-900 text-white px-3 py-2 rounded-lg shadow-lg flex items-center gap-2 animate-in fade-in duration-200 z-50"
          data-testid="key-sequence-indicator"
        >
          <Keyboard className="h-4 w-4" />
          <span className="font-mono">{keySequence}</span>
        </div>
      )}

      {/* Help Dialog */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="max-w-lg" data-testid="keyboard-shortcuts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Keyboard className="h-5 w-5" />
              Keyboard Shortcuts
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4 max-h-[60vh] overflow-y-auto">
            {Object.entries(groupedShortcuts).map(([category, categoryShortcuts]) => (
              <div key={category}>
                <h3 className="text-sm font-semibold text-muted-foreground mb-2">{category}</h3>
                <div className="space-y-2">
                  {categoryShortcuts.map((shortcut, idx) => (
                    <div key={idx} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                      <span className="text-sm">{shortcut.action}</span>
                      <div className="flex items-center gap-1">
                        {shortcut.key.split(' ').map((k, i) => (
                          <React.Fragment key={i}>
                            {i > 0 && <span className="text-muted-foreground text-xs">then</span>}
                            <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono border">{k}</kbd>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm text-muted-foreground">
            <span>Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono border">?</kbd> to show this dialog</span>
            <span>Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs font-mono border">Esc</kbd> to close</span>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * KeyboardShortcutsButton - A button to show keyboard shortcuts help
 */
export function KeyboardShortcutsButton({ onClick }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="gap-1.5 text-muted-foreground"
      data-testid="keyboard-shortcuts-btn"
    >
      <Keyboard className="h-4 w-4" />
      <span className="hidden sm:inline">Shortcuts</span>
      <Badge variant="secondary" className="text-[10px] px-1">?</Badge>
    </Button>
  );
}
